import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList, type PagedListParams} from '@/hooks/common/use-paged-list'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import {track} from '@/lib/telemetry'
import type {components} from '@/lib/api-schema'

export type Obra = components['schemas']['ProyectoRead']
export type ObraEstado = components['schemas']['ProyectoEstado']
export type ObraPeligros = components['schemas']['PeligrosRead']
export type AlcancePeligros = components['schemas']['AlcancePeligrosRead']
export type Asignacion = components['schemas']['AsignacionRead']
export type LaborCamino = components['schemas']['LaborCamino']
export type ContratistaVinculo = components['schemas']['ContratistaVinculoRead']

export const OBRAS_PAGE_SIZE = 10
export const ASIGNACIONES_PAGE_SIZE = 20

function base(businessId: string | undefined) {
  return `/businesses/${businessId}/proyectos`
}

function obraKey(businessId: string | undefined, proyectoId?: string) {
  return proyectoId ? ['obras', businessId, proyectoId] : ['obras', businessId]
}

export function useObras(businessId: string | undefined, params: PagedListParams) {
  return usePagedList<Obra>(['obras', businessId], businessId ? base(businessId) : undefined, params, OBRAS_PAGE_SIZE)
}

export function useObra(businessId: string | undefined, proyectoId: string | undefined) {
  return useQuery({
    queryKey: obraKey(businessId, proyectoId),
    queryFn: async () => (await api.get<Obra>(`${base(businessId)}/${proyectoId}`)).data,
    enabled: !!businessId && !!proyectoId,
  })
}

export type ObraWrite = {
  name: string
  fecha_inicio: string
  fecha_fin: string | null
  sede_ids: string[]
}

export function useCrearObra(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: ObraWrite) => (await api.post<Obra>(base(businessId), input)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['obras', businessId]})
      track('obra_creada', {business_id: businessId})
    },
  })
}

export function useEditarObra(businessId: string, proyectoId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Partial<ObraWrite> & {estado?: ObraEstado}) =>
      (await api.patch<Obra>(`${base(businessId)}/${proyectoId}`, input)).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['obras', businessId]}),
  })
}

export function usePeligrosDeLaObra(businessId: string | undefined, proyectoId: string | undefined) {
  return useQuery({
    queryKey: [...obraKey(businessId, proyectoId), 'peligros'],
    queryFn: async () => (await api.get<ObraPeligros>(`${base(businessId)}/${proyectoId}/peligros`)).data,
    enabled: !!businessId && !!proyectoId,
  })
}

/** Mutación y no consulta: el `alcance_token` que exige la confirmación describe un instante, no un estado cacheable. */
export function useAlcanceDePeligros(businessId: string, proyectoId: string) {
  return useMutation({
    mutationFn: async (riesgoFilaIds: string[]) =>
      (
        await api.post<AlcancePeligros>(`${base(businessId)}/${proyectoId}/peligros/alcance`, {
          riesgo_fila_ids: riesgoFilaIds,
        })
      ).data,
  })
}

export function useConfirmarPeligros(businessId: string, proyectoId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {riesgoFilaIds: string[]; alcanceToken: string}) =>
      (
        await api.post<ObraPeligros>(`${base(businessId)}/${proyectoId}/peligros`, {
          riesgo_fila_ids: input.riesgoFilaIds,
          alcance_token: input.alcanceToken,
        })
      ).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({queryKey: ['obras', businessId]})
      track('obra_peligros_confirmados', {business_id: businessId, peligros: variables.riesgoFilaIds.length})
    },
  })
}

export function useAsignaciones(
  businessId: string | undefined,
  proyectoId: string | undefined,
  historia: boolean,
  page: number
) {
  return useQuery({
    queryKey: [...obraKey(businessId, proyectoId), 'asignaciones', historia, page],
    queryFn: async () =>
      (
        await api.get<Page<Asignacion>>(`${base(businessId)}/${proyectoId}/asignaciones`, {
          params: {page, page_size: ASIGNACIONES_PAGE_SIZE, historia},
        })
      ).data,
    enabled: !!businessId && !!proyectoId,
    placeholderData: (previous) => previous,
  })
}

function useObraMutation<TVars, TData>(businessId: string, proyectoId: string, call: (vars: TVars) => Promise<TData>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: call,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['obras', businessId, proyectoId]}),
  })
}

export function useAsignar(businessId: string, proyectoId: string) {
  return useObraMutation(
    businessId,
    proyectoId,
    async (input: {employeeIds: string[]; desde: string}) =>
      (
        await api.post<Asignacion[]>(`${base(businessId)}/${proyectoId}/asignaciones`, {
          employee_ids: input.employeeIds,
          desde: input.desde,
        })
      ).data
  )
}

export function useDesasignar(businessId: string, proyectoId: string) {
  return useObraMutation(
    businessId,
    proyectoId,
    async (asignacionId: string) =>
      (await api.delete<Asignacion>(`${base(businessId)}/${proyectoId}/asignaciones/${asignacionId}`)).data
  )
}

export function useDeclararLabor(businessId: string, proyectoId: string) {
  return useObraMutation(
    businessId,
    proyectoId,
    async (input: {asignacionId: string; camino: LaborCamino; texto: string | null}) =>
      (
        await api.put<Asignacion>(`${base(businessId)}/${proyectoId}/asignaciones/${input.asignacionId}/labor`, {
          camino: input.camino,
          texto: input.texto,
        })
      ).data
  )
}

export function usePedirLabor(businessId: string, proyectoId: string) {
  return useObraMutation(
    businessId,
    proyectoId,
    async (asignacionId: string) =>
      (await api.post<Asignacion>(`${base(businessId)}/${proyectoId}/asignaciones/${asignacionId}/pedir-labor`, {}))
        .data
  )
}

export function useEscribirVinculos(businessId: string, proyectoId: string) {
  return useObraMutation(
    businessId,
    proyectoId,
    async (input: {employeeIds: string[]; proveedorId: string | null; empresaNombre: string | null}) =>
      (
        await api.post<ContratistaVinculo[]>(`${base(businessId)}/${proyectoId}/vinculos-contratista`, {
          employee_ids: input.employeeIds,
          proveedor_id: input.proveedorId,
          empresa_nombre: input.empresaNombre,
        })
      ).data
  )
}
